import { Fragment, type JSX, forwardRef } from 'react'

import { Switch, SwitchGroup } from '@headlessui/react'
import { MoonIcon, SunIcon } from '@swaggerhub-portal/component-library'
import { clsx } from 'clsx'

import { MODE } from '../DarkMode/constants'
import { useDarkMode } from '../DarkMode/hook'

type DarkModeToggleProps = Omit<JSX.IntrinsicElements['button'], 'onChange'> & {
  checked?: boolean
}

const DarkModeToggle = forwardRef<HTMLButtonElement, DarkModeToggleProps>(({
  className,
  ...props
}, ref) => {
  const { setLocalMode, isDark } = useDarkMode()

  const handleToggle = (checked: boolean) => {
    setLocalMode(checked ? MODE.DARK : MODE.LIGHT)
  }

  return (
    <SwitchGroup as="div">
      <Switch
        data-testid="dark-mode-toggle"
        name="dark-mode-toggle"
        as={Fragment}
        onChange={handleToggle}
        checked={isDark()}
        defaultChecked={isDark()}
        disabled={props.disabled}
      >{({ checked }) => (
          <button
            name="dark-mode-toggle"
            ref={ref}
            className={clsx(
              checked ? 'bg-neutral-98' : 'bg-neutral-30',
              'cursor:pointer relative inline-flex flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out ring-0 focus:outline-none',
              'w-[56px] h-[28px]',
              className,
            )}
            {...props}
          >
            <span
              aria-hidden="true"
              className={clsx(
                'translate-x translate-y translate-y-[4px] pointer-events-none inline-block transform rounded-full shadow ring-0 transition duration-200 ease-in-out focus:outline-none',
                'w-[20px] h-[20px]',
                checked ? 'bg-neutral-40 translate-x-[32px]' : 'bg-neutral-85 translate-x-[4px]'
              )}
            />
            <div className={clsx(
              'translate-x translate-y-[6px]',
              checked ? 'translate-x-[-14px]' : 'transform translate-x-[14px]'
            )}>
              {checked ? <SunIcon /> : <MoonIcon />}
            </div>
          </button>
        )}
      </Switch>
    </SwitchGroup>
  )
})

export default DarkModeToggle